import {
  AttachmentDetail_Download_Format,
  AttachmentDetail_SignatureStatus,
  AttachmentDetail_Status,
} from '@policyfly/protobuf/patrick'

import { bytesToObjectURL } from '@/utils/api'
import { ATTACHMENT_PENDING_KEY } from '@/utils/protobuf'

import type {
  AttachmentDetail,
  AttachmentDetail_ContentLocation,
} from '@policyfly/protobuf/patrick'

/**
 * Whether an attachment is still being uploaded, and so cannot be acted on yet.
 */
export function isPendingAttachment (attachment: AttachmentDetail): boolean {
  return !!attachment[ATTACHMENT_PENDING_KEY]
}

/**
 * Whether an attachment's own content has been loaded, as opposed to still needing to be read.
 * This is the replacement for the legacy `lazy` flag.
 *
 * Only the original is considered, as that is the attachment's own data. Converted formats are
 * served separately, so one of those arriving does not mean the attachment itself has been read.
 */
export function isAttachmentContentLoaded (attachment: AttachmentDetail): boolean {
  const original = attachment.downloads.find((d) => d.format === AttachmentDetail_Download_Format.FORMAT_ORIGINAL)
  return isContentLoaded(original?.location)
}

/**
 * The statuses in which data has yet to arrive.
 * An unset status means the same as processing, so the two are treated alike.
 */
const PROCESSING_STATUSES = new Set<AttachmentDetail_Status>([
  AttachmentDetail_Status.STATUS_UNSPECIFIED,
  AttachmentDetail_Status.STATUS_PROCESSING,
])

/**
 * Whether an attachment or its preview is still being generated, and so is worth polling for.
 */
export function isAttachmentProcessing (attachment: AttachmentDetail): boolean {
  return PROCESSING_STATUSES.has(attachment.status) ||
    (!!attachment.preview && PROCESSING_STATUSES.has(attachment.preview.status))
}

/**
 * The statuses in which a signature has actually been provided,
 * whatever a reviewer has since made of it.
 */
const SIGNED_STATUSES = new Set<AttachmentDetail_SignatureStatus>([
  AttachmentDetail_SignatureStatus.SIGNATURE_STATUS_SIGNED,
  AttachmentDetail_SignatureStatus.SIGNATURE_STATUS_APPROVED,
  AttachmentDetail_SignatureStatus.SIGNATURE_STATUS_REJECTED,
])

/**
 * Whether an attachment has been signed, regardless of whether or not the signature has been reviewed.
 */
export function isAttachmentSigned (attachment: AttachmentDetail): boolean {
  return SIGNED_STATUSES.has(attachment.signatureStatus)
}

/**
 * Whether an attachment has been signed but the signature has yet to be accepted or declined.
 */
export function isSignatureUnconfirmed (attachment: AttachmentDetail): boolean {
  return attachment.signatureStatus === AttachmentDetail_SignatureStatus.SIGNATURE_STATUS_SIGNED
}

/**
 * The mime type of each format an attachment can be converted to.
 * The original is absent, as it keeps whatever mime type the attachment itself reports.
 */
const DOWNLOAD_FORMAT_MIME_TYPES: Record<AttachmentDetail_Download_Format, string> = {
  [AttachmentDetail_Download_Format.FORMAT_PDF]: 'application/pdf',
  [AttachmentDetail_Download_Format.FORMAT_XLSX]: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  [AttachmentDetail_Download_Format.FORMAT_DOCX]: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  [AttachmentDetail_Download_Format.FORMAT_ORIGINAL]: '',
  [AttachmentDetail_Download_Format.FORMAT_UNSPECIFIED]: '',
}

/**
 * Resolves a {@link AttachmentDetail_ContentLocation} to a url usable as an `href` or `src`.
 *
 * Bytes are materialised into an object url the first time they are used, and written back to the
 * location url for reuse. Revoke them with {@link revokeContentLocation}.
 *
 * @returns The url, or `null` while the content has not been loaded.
 */
export function contentLocationToUrl (
  location: AttachmentDetail_ContentLocation | undefined,
  mimeType?: string,
): string | null {
  if (!location) return null
  switch (location.location.oneofKind) {
    case 'url':
      return location.location.url || null
    case 'bytes': {
      const url = bytesToObjectURL(location.location.bytes, mimeType)
      location.location = { oneofKind: 'url', url }
      return url
    }
    default:
      return null
  }
}

/**
 * Revokes the object url a location holds if applicable and returns the location to being unloaded.
 *
 * **NOTE:** This is a lossy conversion as the previous location details are lost, however we currently only
 * load an attachment in its entirety so all locations are always regenerated. In future when we need
 * to load individual locations this will need to be fixed.
 */
export function revokeContentLocation (location: AttachmentDetail_ContentLocation | undefined): void {
  const url = locationUrl(location)
  if (!url?.startsWith('blob:')) return
  URL.revokeObjectURL(url)
  location!.location = { oneofKind: undefined }
}

/**
 * Revokes every object url an attachment has materialised.
 * Call this when dropping an attachment, as its urls cannot be reached again afterwards.
 */
export function revokeAttachmentContent (attachment: AttachmentDetail): void {
  revokeContentLocation(attachment.preview?.location)
  for (const download of attachment.downloads) {
    revokeContentLocation(download.location)
  }
}

/**
 * Returns the url to render an attachment's preview from, if it has one that has been loaded.
 *
 * Will materialise any bytes into an object url and store it on the attachment,
 * see {@link contentLocationToUrl}. Revoke it with {@link revokeAttachmentContent}.
 */
export function attachmentPreviewUrl (attachment: AttachmentDetail): string | null {
  return contentLocationToUrl(attachment.preview?.location, DOWNLOAD_FORMAT_MIME_TYPES[AttachmentDetail_Download_Format.FORMAT_PDF])
}

/**
 * The url to download an attachment in the requested format, if it offers that format
 * and the content has been loaded.
 *
 * Will materialise any bytes into an object url and store it on the attachment,
 * see {@link contentLocationToUrl}. Revoke it with {@link revokeAttachmentContent}.
 */
export function attachmentDownloadUrl (
  attachment: AttachmentDetail,
  format: AttachmentDetail_Download_Format,
): string | null {
  const download = attachment.downloads.find((d) => d.format === format)
  if (!download) return null
  return contentLocationToUrl(download.location, DOWNLOAD_FORMAT_MIME_TYPES[format] || attachment.mimeType)
}

/**
 * Applies loaded content from a cached attachment onto the incoming version of it.
 *
 * Content the incoming attachment has already loaded always wins, so a freshly read attachment
 * keeps its own data. Anything it has yet to load falls back to the cached content, so re-listing
 * does not discard what has already been fetched.
 *
 * Object urls left unused are revoked.
 */
export function preserveAttachmentContent (
  incoming: AttachmentDetail,
  cached: AttachmentDetail | undefined,
): AttachmentDetail {
  if (!cached) return incoming

  if (incoming.preview) preserveContentLocation(incoming.preview, cached.preview)
  // an attachment that no longer offers a preview will never reach the cached one again
  else revokeContentLocation(cached.preview?.location)

  for (const download of incoming.downloads) {
    preserveContentLocation(download, cached.downloads.find((d) => d.format === download.format))
  }
  for (const cachedDownload of cached.downloads) {
    const stillOffered = incoming.downloads.some((d) => d.format === cachedDownload.format)
    if (!stillOffered) revokeContentLocation(cachedDownload.location)
  }

  return incoming
}

/**
 * Calls {@link preserveAttachmentContent} on a list of attachments,
 * revoking the content of any cached attachment that is no longer listed.
 *
 * @param retained Attachments that are still held elsewhere. Lists share their attachment objects,
 * so one dropping an attachment does not mean its content can be released.
 */
export function preserveAttachmentListContent (
  incoming: AttachmentDetail[],
  existing: AttachmentDetail[],
  retained: AttachmentDetail[] = [],
): AttachmentDetail[] {
  const existingById = new Map(existing.map((attachment) => [attachment.id, attachment]))
  const keptIds = new Set([...incoming, ...retained].map((attachment) => attachment.id))

  for (const attachment of incoming) {
    preserveAttachmentContent(attachment, existingById.get(attachment.id))
  }
  for (const attachment of existing) {
    if (!keptIds.has(attachment.id)) revokeAttachmentContent(attachment)
  }

  return incoming
}

/**
 * Holds the content location of a preview or a download.
 */
interface ContentHolder {
  location?: AttachmentDetail_ContentLocation
}

/**
 * Keeps whichever of the two holders has loaded content, preferring the incoming one
 * and revoking an object url the cached one is left holding.
 */
function preserveContentLocation (incoming: ContentHolder, cached: ContentHolder | undefined): void {
  if (!cached) return
  if (!isContentLoaded(incoming.location)) {
    if (isContentLoaded(cached.location)) incoming.location = cached.location
    return
  }
  if (locationUrl(cached.location) !== locationUrl(incoming.location)) {
    revokeContentLocation(cached.location)
  }
}

/**
 * Whether a location points at content that is loaded and available to use.
 * This can be considered a check for whether the attachment is still in "lazy" state.
 */
export function isContentLoaded (location: AttachmentDetail_ContentLocation | undefined): boolean {
  switch (location?.location.oneofKind) {
    case 'url': return !!location.location.url
    case 'bytes': return !!location.location.bytes.length
    default: return false
  }
}

/**
 * Returns the url a location already holds without converting bytes like {@link contentLocationToUrl} does.
 */
function locationUrl (location: AttachmentDetail_ContentLocation | undefined): string | null {
  return location?.location.oneofKind === 'url' ? location.location.url : null
}
